import {
  AfterViewInit,
  Component,
  DestroyRef,
  HostListener,
  inject,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
  TemplateRef,
  Type
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';

import { ItemDetailsPage } from 'src/pages/more/edit-address/room-item-details/room-item-details';
import { ReportSettingsPage } from 'src/pages/dashboard/report-settings/report-settings';
import { ToDosRoomPage } from 'src/pages/to-dos/room/room';
import { ToDosRenameListPage } from 'src/pages/to-dos/rename-list/rename-list';
import { ToDosPage } from 'src/pages/to-dos/to-dos';
import { ConciergePage } from 'src/pages/concierge/concierge';
import { ScheduleListPage } from 'src/pages/schedule/schedule-list/schedule-list';
import { ProAddedPage } from 'src/pages/my-pros/pro-added/pro-added';
import { PropertyOptionsPage } from 'src/pages/more/edit-address/property-options/property-options';
import { JobPage } from 'src/pages/schedule/job/job';
import { EmployerEditEmployeePage } from 'src/pages/employer/edit-employee/edit-employee';
import { SummaryOfDayPage } from 'src/pages/schedule/summary-of-day/summary-of-day';
import { AutomaticBookingAddressPage } from 'src/pages/more/automatic-booking/automatic-booking-address/automatic-booking-address';
import { BillingHistoryPage } from 'src/pages/billing-history/billing-history';
import { NotificationCenterPage, notificationCenterDropDownMenu } from 'src/pages/notification-center/notification-center';
import { FormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { BREAK_LARGE_RESOLUTION } from 'android/app/src/constants/app.contants';
import { MessageThreadPage, messageThreadDropDownMenu } from 'src/pages/messages/message-thread/message-thread';
import { ActiveIntegrationsPage } from 'src/pages/integrations/active-integrations/active-integrations';
import { TaskPage } from 'src/pages/tasks/task/task';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { OnboardingComponent } from 'src/shared/components/onboarding/onboarding';
import { SuccessPage } from 'src/shared/pages/success/success';

@Component({
    selector: 'app-right-side-dialog-content',
    template: `
    <div class="modal-backdrop modal-fade-in" [class.full-overlay]="isFullOverlay" (click)="onBackdropClick($event)">
      <div
        class="centered-modal modal-scale-in"
        [id]="'right-side-dialog'"
        [ngClass]="{'compact-mode': isCompactMode, 'small-display': isSmallDisplay}"
        [ngStyle]="{'--dynamic-background-color': backgroundColor}"
        (click)="$event.stopPropagation()">
        @if (!isCompactMode) {
        <tidy-row class="right-side-panel-header" [ngStyle]="{'background-color': backgroundColor}" [alignLastItemRight]="true">
          <div class="right-side-panel-title">
            @if (canGoBack) {
              <img
                class="back-button"
                (click)="goBack()"
                src="assets/img/white-back-arrow.svg" />
            }
            @if ((rightSidePanelService.pageTitle$ | async); as pageTitle) {
              <div class="title-text">
                <tidy-text
                  [header]="pageTitle | translate">
                </tidy-text>
                @if ((rightSidePanelService.pageTitleTooltip$ | async); as pageTitleTooltip) {
                  <img
                    class="title-info-icon"
                    src="assets/svg/information-circle-outline.svg"
                    alt=""
                    [matTooltip]="pageTitleTooltip | translate"
                    [matTooltipPosition]="'below'"
                    matTooltipClass="menu-item-tooltip below" />
                }
              </div>
            } @else {
              <ion-skeleton-text animated style="width: 150px; height: 24px; border-radius: 4px;"></ion-skeleton-text>
            }
            @if (rightSidePanelService.headerToggleVisible$ | async) {
              <tidy-toggle
                [toggled]="rightSidePanelService.headerToggleValue$ | async"
                (toggleChanged)="onHeaderToggleChanged($event)"
                class="header-toggle primary-button-theme">
              </tidy-toggle>
              @if (rightSidePanelService.headerSpinnerVisible$ | async) {
                <ion-spinner name="lines" class="header-loading-spinner" style="margin-left: 10px;"></ion-spinner>
              }
            }
          </div>
          <div style="display: flex; align-items: center;">
            @if ((rightSidePanelService.isLoading$ | async) || (rightSidePanelService.headerSpinnerVisible$ | async)) {
              <ion-spinner name="lines" class="header-loading-spinner" style="margin-right: 15px" />
            }
            <div class="menu-icon-container" [ngClass]="{'fit-drop-down': rightSidePanelService.currentComponentData?.dropDownMenu}">
              @if (rightSidePanelService.currentComponentData?.dropDownMenu) {
                <tidy-drop-down-menu class="drop-down-menu">
                  @for (item of rightSidePanelService.currentComponentData?.dropDownMenu; track item) {
                    <tidy-drop-down-menu-item
                      (click)="dropDownMenuClicked(item.action)">
                      {{item.label}}
                    </tidy-drop-down-menu-item>
                  }
                </tidy-drop-down-menu>
              }
              @for (headerIcon of (rightSidePanelService.headerIcons$ | async); track headerIcon.id) {
                @if (headerIcon.label) {
                  <div class="header-action-button" (click)="headerIconClicked(headerIcon.id)" [title]="headerIcon.tooltip || ''">
                    <img class="header-action-icon" [src]="headerIcon.icon" />
                    <span class="header-action-label">{{headerIcon.label}}</span>
                  </div>
                } @else {
                  <img
                    class="header-action-icon"
                    [src]="headerIcon.icon"
                    [title]="headerIcon.tooltip || ''"
                    (click)="headerIconClicked(headerIcon.id)" />
                }
              }
              @if ((rightSidePanelService.headerDropdownMenu$ | async)?.length > 0) {
                <tidy-drop-down-menu class="header-dropdown-menu">
                  @for (item of (rightSidePanelService.headerDropdownMenu$ | async); track item.action) {
                    <tidy-drop-down-menu-item
                      (click)="headerDropdownMenuClicked(item.action)">
                      {{item.label}}
                    </tidy-drop-down-menu-item>
                  }
                </tidy-drop-down-menu>
              }
              @if (rightSidePanelService?.linkData?.icon) {
                <img class="link-icon" [src]="rightSidePanelService?.linkData?.icon" (click)="linkActionClicked()"/>
              }
              <div class="close-icon-wrap">
                <img
                  class="close-icon"
                  src="assets/img/white-close.svg"
                  (click)="onCloseClick()"
                  (mousedown)="startSkipHold()"
                  (mouseup)="cancelSkipHold()"
                  (mouseleave)="cancelSkipHold()"
                  (touchstart)="startSkipHold()"
                  (touchend)="cancelSkipHold()" />
                @if (showSkipMenu) {
                  <div class="qa-skip-menu" (click)="triggerSkip(); $event.stopPropagation()">Skip</div>
                }
              </div>
            </div>
          </div>
        </tidy-row>
        @if ((rightSidePanelService.pageSubtitle$ | async); as pageSubtitle) {
          <div class="dialog-subtitle">
            @if ((rightSidePanelService.pageSubtitleIcon$ | async); as pageSubtitleIcon) {
              <img [src]="pageSubtitleIcon" class="dialog-subtitle-icon" alt="" />
            }
            <tidy-text [helper]="pageSubtitle"></tidy-text>
          </div>
        }
        }
        @if (!isCompactMode) {
          @if ({
            value: rightSidePanelService.isLoading$ | async
            }; as isLoading) {
            @if (isLoading.value) {
              <div class="desktop">
                <ion-skeleton-text
                  animated
                  slot="start"
                style="width: 100%; height: 100vh; margin-top: -10px"></ion-skeleton-text>
              </div>
            }
          }
        }
        <div class="scrollable-content" cdkScrollable>
          <ng-template #dynamicComponentContainer></ng-template>
        </div>
      </div>
    </div>
    `,
    styleUrls: ['dynamic-dialog-container.scss'],
    standalone: false
})

export class DialogContentComponent implements AfterViewInit, OnChanges {
  private destroyRef = inject(DestroyRef);

  @ViewChild('dynamicComponentContainer', { read: ViewContainerRef })
  container: ViewContainerRef;
  pageTitle: string;
  canGoBack: boolean;
  messageForm: UntypedFormGroup;
  submitted = false;
  backgroundColor: string;
  isFullOverlay = false;
  isCompactMode = false;
  isSmallDisplay = false;

  // QA-only: holding the X button for 5s reveals a "Skip" menu that fast-forwards the
  // current page's gated step (only on pages that registered customSkipAction$).
  showSkipMenu = false;
  private skipHoldTimer: any = null;
  private skipHoldTriggered = false;

  @HostListener('window:resize')
  onResize() {
    const isDesktop = window.innerWidth > BREAK_LARGE_RESOLUTION;
    if (!isDesktop) {
      this.dismissModal();
    }
  }

  constructor(
    public rightSidePanelService: RightSidePanelService,
    private router: Router,
    private fb: FormBuilder,
    private storage: TidyStorage
  ) {
    this.messageForm = this.fb.group({
      message: ['', Validators.required],
    });
    this.backgroundColor = '#1D2125';
    this.isFullOverlay = this.router.url.includes('getting-started');
  }

  ngAfterViewInit(): void {
    this.watchCurrentComponentChanges();
    this.watchRouterEvents();
    this.watchHistoryChanges();
    this.watchCustomBackAction();
  }

  ngOnChanges(_changes: SimpleChanges): void {
    this.updateScrollableContent();
  }

  // ---- QA-only "Skip" affordance (5s long-press on the X) ----

  startSkipHold(): void {
    this.cancelSkipHold();
    // Only arm on pages that opted in (registered a skip action).
    if (!this.rightSidePanelService.customSkipAction$.getValue()) { return; }
    this.skipHoldTimer = setTimeout(() => {
      this.skipHoldTriggered = true;
      this.showSkipMenu = true;
    }, 5000);
  }

  cancelSkipHold(): void {
    if (this.skipHoldTimer) {
      clearTimeout(this.skipHoldTimer);
      this.skipHoldTimer = null;
    }
  }

  // The X's click fires after a long-press releases — suppress the close in that case so
  // the revealed Skip menu stays. A normal quick click closes the dialog as usual.
  onCloseClick(): void {
    if (this.skipHoldTriggered) {
      this.skipHoldTriggered = false;
      return;
    }
    this.showSkipMenu = false;
    this.dismissModal();
  }

  triggerSkip(): void {
    const skip = this.rightSidePanelService.customSkipAction$.getValue();
    this.showSkipMenu = false;
    this.skipHoldTriggered = false;
    if (skip) { skip(); }
  }

  watchHistoryChanges(): void {
    this.rightSidePanelService.historyContext$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.checkCanGoBack();
    });
  }

  watchCustomBackAction(): void {
    this.rightSidePanelService.customBackAction$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.checkCanGoBack();
    });
  }

  updateScrollableContent(): void {
    setTimeout(() => {
      const customRightColumn = document.querySelector('.custom-right-column') as HTMLElement;
      const scrollableContent = document.querySelector('.scrollable-content') as HTMLElement;
      if (!scrollableContent) {
        return;
      }
      if (customRightColumn) {
        scrollableContent.style.overflow = 'hidden';
      } else {
        scrollableContent.style.overflow = 'auto';
      }
    }, 500);
  }

  watchCurrentComponentChanges(): void {
    this.rightSidePanelService.currentComponent$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.checkCompactMode();
      this.loadOrReplaceComponent();
      this.checkCanGoBack();
      this.updateScrollableContent();
    });
  }

  checkCompactMode(): void {
    const currentComponent = this.rightSidePanelService.currentComponent$.getValue();
    this.isCompactMode = currentComponent === SuccessPage;
    this.isSmallDisplay = !!this.rightSidePanelService.currentComponentData?.smallDisplay;
    this.rightSidePanelService.isCompactMode$.next(this.isCompactMode);
  }

  watchRouterEvents(): void {
    this.router.events.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((event) => {
      const shouldOpenBookingPage = localStorage.getItem('shouldOpenBookingPage') === 'true';
      if (event instanceof NavigationStart && !shouldOpenBookingPage) {
        this.dismissModal();
      }
    });
  }

  /**
   * Check if the user can go back
   */
  async checkCanGoBack(): Promise<void> {
    const customBack = this.rightSidePanelService.customBackAction$.getValue();
    if (customBack) {
      this.canGoBack = true;
      return;
    }
    const hideBackButton = await this.checkIfShouldHideBackButton();
    const historyContext = this.rightSidePanelService.historyContext$.getValue();
    this.canGoBack = historyContext?.length > 1 && !hideBackButton;
  }

  async checkIfShouldHideBackButton() {
    const currentComponent = this.rightSidePanelService.currentComponent$.getValue();
    const dialogParams = await this.storage.retrieve('dialog-params');
    if (dialogParams?.cameFromSubscribePage || dialogParams?.hideBackButton) {
      return true;
    }
    if (dialogParams?.cameFromRoute === 'task' || currentComponent === OnboardingComponent || currentComponent === ActiveIntegrationsPage || currentComponent === BillingHistoryPage || currentComponent === EmployerEditEmployeePage || currentComponent === ToDosPage || currentComponent === ItemDetailsPage && this.rightSidePanelService.currentComponentData?.cameFromEditItem || currentComponent === ReportSettingsPage || currentComponent === ConciergePage || (currentComponent === AutomaticBookingAddressPage && this.rightSidePanelService.currentComponentData?.cameFromAddIntegration) || currentComponent === ScheduleListPage || currentComponent === ProAddedPage || currentComponent === PropertyOptionsPage) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * Go back to the previous component
   */
  async goBack(): Promise<void> {
    const customBack = this.rightSidePanelService.customBackAction$.getValue();
    if (customBack) {
      customBack();
      this.checkCanGoBack();
      return;
    }
    const historyContext = this.rightSidePanelService.historyContext$.getValue();
    historyContext.pop();
    const hardCodedBackPage = this.getHardCodedBackPage();
    const previousComponent = historyContext[historyContext.length - 1];
    this.rightSidePanelService.historyContext$.next(historyContext);
    const page = hardCodedBackPage ? hardCodedBackPage : previousComponent;
    this.rightSidePanelService.clearHeaderIcons();
    this.rightSidePanelService.clearHeaderDropdownMenu();
    this.rightSidePanelService.setLinkActionData(null);
    if (page === NotificationCenterPage) {
      this.rightSidePanelService.dropDownMenuAction$.next(null);
      this.rightSidePanelService.currentComponent$.next(page);
      this.rightSidePanelService.currentComponentData = { dropDownMenu: notificationCenterDropDownMenu };
    } else if (page === MessageThreadPage) {
      this.rightSidePanelService.dropDownMenuAction$.next(null);
      this.rightSidePanelService.currentComponent$.next(page);
      this.rightSidePanelService.currentComponentData = { dropDownMenu: messageThreadDropDownMenu };
    } else {
      this.rightSidePanelService.currentComponentData = {};
      this.rightSidePanelService.currentComponent$.next(page);
    }
    this.loadOrReplaceComponent();
    this.checkCanGoBack();
    this.updateScrollableContent();
  }

  getHardCodedBackPage() {
    const currentComponent = this.rightSidePanelService.currentComponent$.getValue();
    if (currentComponent === ToDosRoomPage) {
      return ToDosPage;
    } else if (currentComponent === ToDosRenameListPage) {
      // Resolve an explicit destination so back works at any panel stack depth.
      // Without it a shallow stack pops to undefined and blanks the panel.
      return ToDosPage;
    } else if (currentComponent === JobPage) {
      const cameFromSummaryOfDay = localStorage.getItem('jobPageBackPath') === 'summary-of-day';
      const cameFromNotificationCenter = localStorage.getItem('jobBackPage') === 'notification-center';
      const cameFromTaskPage = localStorage.getItem('jobBackPage') === 'task';
      if (cameFromSummaryOfDay) {
        return SummaryOfDayPage;
      } else if (cameFromNotificationCenter) {
        return NotificationCenterPage;
      } else if (cameFromTaskPage) {
        return TaskPage;
      }else {
        return ScheduleListPage;
      }
    } else {
      return null;
    }
  }

  /**
   * Dismiss the modal
   */
  dismissModal(): void {
    localStorage.setItem('isPrimaryLayer', 'true');
    this.rightSidePanelService.closeRightSidePanel();
  }

  dropDownMenuClicked(action: string): void {
    this.rightSidePanelService.emitDropDownMenuAction(action);
  }

  linkActionClicked(): void {
    this.rightSidePanelService.emitLinkAction();
  }

  headerIconClicked(iconId: string): void {
    this.rightSidePanelService.emitHeaderIconClicked(iconId);
  }

  headerDropdownMenuClicked(action: string): void {
    this.rightSidePanelService.emitDropDownMenuAction(action);
  }

  onHeaderToggleChanged(value: boolean): void {
    this.rightSidePanelService.emitHeaderToggleChanged(value);
  }

  onHeaderFilterChange(event: any): void {
    const value = event?.value ?? event;
    this.rightSidePanelService.emitHeaderFilterChanged(value);
  }

  onBackdropClick(event: MouseEvent): void {
    this.dismissModal();
  }

  /**
   * Load or replace the component in the dialog
   */
  async loadOrReplaceComponent(): Promise<void> {
    const currentComponent = this.rightSidePanelService.currentComponent$.getValue();
    this.container.clear();
    if (!currentComponent) {
      return;
    }
    if ('createEmbeddedView' in currentComponent) {
      this.container.createEmbeddedView(currentComponent as TemplateRef<any>);
    } else {
      const componentRef = this.container.createComponent(currentComponent as Type<unknown>);
      (componentRef.instance as any).data = this.rightSidePanelService.currentComponentData;
    }
    this.updateScrollableContent();
  }

}

export interface RightSideDialogData {
  [key: string]: any;
  dropDownMenu?: {
    label: string;
    action: string;
  }[];
};
